import React, { useState } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { LoadingIndicator } from 'lib/components';
import { Flex } from 'lib/components/styles/layout';
import { Button, Typography } from 'react-covideo-common';
import { MdAdd, MdClose } from 'react-icons/md';
import { BackgroundList } from 'app/pages/ai-configuration/aiAssistants/backgrounds/BackgroundList';
import { ICreateSpotlightFormikValues } from '../types';
import { useTheme } from 'styled-components/macro';
import { IBackgroundResponse } from 'lib/api/backgrounds/useQueryBackgrounds';
import { SCRIPT_MAX_LENGTH, SpotlightSchema } from './schema';
import { FormikTextAreaField } from 'lib/components/formik/FormikTextAreaField';
import { useTranslation } from 'react-i18next';
import { PROMPT_TYPE } from 'react-covideo-ai-assist';
import { MagicButton } from 'lib/components/MagicButton';
import { CAROUSEL_ITEMS_PER_PAGE } from '../constants';
import { FullScreenModal } from 'lib/components/FullScreenModal';
import {
  AI_FLEX_VIDEO_SCRIPTS,
  fillScriptVariables,
} from 'lib/const/AIFlexVideoScripts';
import {
  FormikDropdownField,
  FormikDropdownOption,
} from 'lib/components/formik/FormikDropdown';
import { IAIAssistantResponse } from 'lib/api/avatars/customerAvatars/useQueryCustomerAvatars';
import { useAuth } from 'lib/context/auth';
import { getDisplayName } from 'lib/utils/functions';
import { useGooglePlaceDetails } from 'lib/hooks/useGooglePlaceDetails';
import { AISpotLightConfirmationModal } from '../../AISpotLightConfirmationModal';
import { AISpotlightUsage } from '../AISpotlightUsage';
import { useGetSpotlightsLeftCount } from 'lib/api/spotlight/useGetSpotlightLeftCount';

interface IAISpotlightFormProps {
  onSave: (
    values: ICreateSpotlightFormikValues,
    { setFieldValue }: FormikHelpers<ICreateSpotlightFormikValues>
  ) => Promise<void>;
  onClose: () => void;
  backgrounds: IBackgroundResponse[];
  toggleShowBackgroundUploadModal: () => void;
  isLoading: boolean;
  isFetchingBackgrounds: boolean;
  defaultBackgroundId: number;
  defaultAvatar: IAIAssistantResponse | undefined;
}

const initialValues: ICreateSpotlightFormikValues = {
  text: '',
  backgroundId: 0,
};

export const AISpotlightForm = ({
  onSave,
  onClose,
  backgrounds,
  toggleShowBackgroundUploadModal,
  isLoading,
  isFetchingBackgrounds,
  defaultBackgroundId,
  defaultAvatar,
}: IAISpotlightFormProps) => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();
  const { userData } = useAuth();
  const { placeDetails } = useGooglePlaceDetails(
    userData?.customer?.mapIframeEmbedCode
  );

  const {
    data: spotlightsUsage,
    isLoading: isLoadingSpotlightsUsage,
    isFetching: isFetchingSpotlightsUsage,
  } = useGetSpotlightsLeftCount();

  const scriptOptions = AI_FLEX_VIDEO_SCRIPTS.map(script => ({
    value: script.id,
    label: t(script.title),
  }));

  const columns = backgrounds.length > 0 ? CAROUSEL_ITEMS_PER_PAGE : 1;

  if (isLoading) {
    return (
      <FullScreenModal style={{ zIndex: 100 }}>
        <Flex
          width='100%'
          height='100vh'
          justifyContent='center'
          alignItems='center'
        >
          <LoadingIndicator isLoading />
        </Flex>
      </FullScreenModal>
    );
  }

  return (
    <>
      <Formik
        initialValues={{
          ...initialValues,
          backgroundId: defaultBackgroundId,
        }}
        onSubmit={onSave}
        validationSchema={SpotlightSchema(t)}
        validateOnMount
      >
        {({ isSubmitting, setFieldValue, handleSubmit, values, isValid }) => {
          const onDefaultBackgroundChange = (backgroundId: number) => {
            setFieldValue('backgroundId', backgroundId);
          };

          const handleScriptSelect = (option: FormikDropdownOption) => {
            if (!option?.value) {
              setFieldValue('text', '');
              return;
            }
            const selectedScript = AI_FLEX_VIDEO_SCRIPTS.find(
              script => script.id === option.value
            );
            if (selectedScript) {
              const websiteUrl = userData?.customer?.url
                ? userData.customer.url.replace(/^https?:\/\//, '')
                : '';

              const filledScript = fillScriptVariables(t, selectedScript.text, {
                // fill fillable
                'VA Name': defaultAvatar?.avatarName,
                'Dealership Name': userData?.customer?.business,
                'GM Name': getDisplayName([
                  userData?.firstName,
                  userData?.lastName,
                ]),
                'Website URL': websiteUrl,
                'Phone Number': userData?.customer?.phone1,
                'Address/Location': placeDetails?.formattedAddress,
                'City/Community Name': placeDetails?.city,
              });
              setFieldValue('text', filledScript);
            }
          };

          const isSubmitButtonDisabled =
            isSubmitting ||
            isLoading ||
            !isValid ||
            spotlightsUsage?.left === 0;

          const isCloseButtonDisabled =
            isSubmitting || isLoading || isFetchingBackgrounds;

          return (
            <FullScreenModal
              style={{ zIndex: 100 }}
              extendedStyles={{
                body: { padding: '0 0 32px 0' },
              }}
              headerCenter={
                <Typography fontWeight={600} variant='subheading'>
                  {t('Create AI Flex Video')}
                </Typography>
              }
              headerRight={
                <Button
                  type='submit'
                  variant='primary'
                  text={t('Create AI Flex Video')}
                  onClick={() => setShowConfirmationModal(true)}
                  disabled={isSubmitButtonDisabled}
                />
              }
              headerLeft={
                <Button
                  variant='transparent'
                  icon={<MdClose size='18px' />}
                  onClick={onClose}
                  disabled={isCloseButtonDisabled}
                />
              }
            >
              <Form
                onSubmit={handleSubmit}
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <AISpotlightUsage
                  isFetching={isFetchingSpotlightsUsage}
                  isLoading={isLoadingSpotlightsUsage}
                  spotlightsUsage={spotlightsUsage}
                />
                {/* Form Body */}
                <Flex
                  justifyContent='flex-start'
                  alignItems='center'
                  padding='24px 0 0 0px'
                >
                  <Flex style={{ maxWidth: '1120px' }} gap='33px'>
                    <Flex
                      gap='16px'
                      width='100%'
                      flexDirection='row'
                      justifyContent='space-between'
                      alignItems='center'
                    >
                      <Typography variant='subheading' fontWeight={600}>
                        {t('AI Flex Video Script')}
                      </Typography>
                      <MagicButton
                        userInput={values.text || ''}
                        defaultPromptType={PROMPT_TYPE.AI_FLEX_VIDEO}
                        handleSubmitGeneratedContent={(
                          generatedContent: string
                        ) => {
                          setFieldValue('text', generatedContent);
                        }}
                        margin='0'
                        hoverPosition='below'
                        text={t('Script Assist')}
                      />
                    </Flex>
                    <Flex gap='0px'>
                      <FormikDropdownField
                        name='scriptSelection'
                        options={scriptOptions}
                        placeholder={t('Select a script template...')}
                        onChange={handleScriptSelect}
                        isClearable={true}
                        isSearchable={false}
                        extendStyles={{
                          inputWrapper: { minWidth: '250px' },
                        }}
                      />
                      <FormikTextAreaField
                        name='text'
                        placeholder={t(
                          'Start typing your AI Flex Video script...'
                        )}
                        label=''
                        isRequired={false}
                        extendStyles={{
                          textAreaStyles: { minHeight: '212px' },
                        }}
                        showCounter
                        maxLength={SCRIPT_MAX_LENGTH}
                        boldBracketedText={true}
                        disabled={
                          isSubmitting || isLoading || isFetchingBackgrounds
                        }
                      />
                    </Flex>

                    <Flex
                      justifyContent='space-between'
                      flexDirection='row'
                      alignItems='center'
                      width='100%'
                    >
                      <Typography variant='subheading' fontWeight={600}>
                        {t('AI Flex Video Background')}
                      </Typography>
                      <Button
                        text={t('Add Background')}
                        icon={<MdAdd color={theme.colors.white[100]} />}
                        onClick={toggleShowBackgroundUploadModal}
                        disabled={isLoading || isFetchingBackgrounds}
                      />
                    </Flex>

                    <BackgroundList
                      backgrounds={backgrounds || []}
                      selectedBackground={values.backgroundId}
                      isLoading={isFetchingBackgrounds}
                      onDefaultBackgroundChange={onDefaultBackgroundChange}
                      selectedText={t('Selected')}
                      unselectedText={t('Select')}
                      extendStyles={{
                        card: {
                          cardWrapperStyles: {
                            flexBasis: 0,
                            flexGrow: 1,
                          },
                        },
                        list: {
                          wrapperStyles: {
                            display: 'grid',
                            gridTemplateColumns: `repeat(${columns}, 1fr)`,
                            columnGap: 10,
                            boxSizing: 'border-box',
                          },
                        },
                      }}
                    />
                  </Flex>
                </Flex>
                {showConfirmationModal && (
                  <AISpotLightConfirmationModal
                    onModalClose={() => setShowConfirmationModal(false)}
                    onConfirm={() => {
                      handleSubmit();
                    }}
                    disabled={isSubmitting || isLoading || !isValid}
                  />
                )}
              </Form>
            </FullScreenModal>
          );
        }}
      </Formik>
    </>
  );
};
